import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';
import { stripMarkdown } from 'src/common/utils/stripMarkdown';
import { useAppContainerContext } from 'src/UI/app-container';
import { ChatMode } from '../chat/types';
import { SiestaAction, SiestaActionType } from '../types';
import { useClients } from 'src/clients/hooks/useClients';
import { useClientsPaginated } from 'src/clients/hooks/useClientsPaginated';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFlags } from 'src/feature-flags/FlagsHook';
import type { DocumentEditOwner } from 'src/document-editor/documentOwner';

type ChatAction = Extract<SiestaAction, { type: typeof SiestaActionType.CHAT }>;

type SiestaActionsConfig = {
  [pageType: string]: ChatAction[];
};

const SIESTA_ACTIONS_CONFIG: SiestaActionsConfig = {
  'suitability-report-edit': [
    {
      type: SiestaActionType.CHAT,
      id: 'capacity-for-loss',
      label:
        "Explain the client's capacity for loss based on their current investment assets and cashflow",
    },
    {
      type: SiestaActionType.CHAT,
      id: 'suitability-rationale',
      label: 'Strengthen the suitability rationale for the recommended funds',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'cashflow-narrative',
      label: 'Add more narrative linking cashflows to objectives',
    },
  ],
  'data-export-edit': [
    {
      type: SiestaActionType.CHAT,
      id: 'remove-empty-rows-columns',
      label: 'Remove empty rows and columns from tables to improve readability',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'consolidate-sparse-sections',
      label: 'Consolidate sparse sections with few entries into a cleaner layout',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'replace-placeholders',
      label: 'Replace placeholder values with a consistent format',
    },
  ],
  'meeting-agenda-edit': [
    {
      type: SiestaActionType.CHAT,
      id: 'add-action-points',
      label: 'Add action points and next steps for each agenda topic',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'add-client-data-references',
      label: 'Include specific client data references to support discussion points',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'expand-objectives',
      label: 'Expand objectives section with measurable outcomes to review',
    },
  ],
  'meeting-note-edit': [
    {
      type: SiestaActionType.CHAT,
      id: 'simplify-language',
      label: 'Simplify language',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'summarise',
      label: 'Summarise',
    },
    {
      type: SiestaActionType.CHAT,
      id: 'make-more-formal',
      label: 'Make more formal',
    },
  ],
};

type UseSuggestedActionsParams = {
  documentId?: string;
  documentContent?: string;
  clientId?: string;
  focusedClientId?: string;
  familyId?: string;
  conversationId?: string;
};

export type GenerateSuggestionsResponse = {
  suggestions: string[];
};

export function useSuggestedActions(params?: UseSuggestedActionsParams): {
  actions: SiestaAction[];
  isLoading: boolean;
} {
  const { contentMeta } = useAppContainerContext();
  const pageType = contentMeta?.pageType;
  const isDocumentMode = contentMeta?.chatMode === ChatMode.DOCUMENT;
  const {
    documentId,
    documentContent,
    clientId: paramClientId,
    focusedClientId,
    familyId,
    conversationId,
  } = params ?? {};
  const isFamilyDocumentMode =
    isDocumentMode && familyId !== undefined && paramClientId === undefined;

  const { flagEnabledPaginatedClients } = useFlags();
  const { clientId: contextClientId } = useCurrentClientIfAvailable();
  const targetClientId = paramClientId ?? focusedClientId ?? contextClientId ?? null;

  // On a family profile the default actions must open the document flows against the family, not a
  // client. The flow routes are always client-scoped, so anchor the URL on the member focused via
  // ?member= (falling back to the family's main member) and carry the familyId so the flow's first
  // step preselects the family (matching how the family documents panel launches these flows).
  const [searchParams] = useSearchParams();
  const focusedFamilyMemberId = searchParams.get('member');
  const familyDefaultMode = !isDocumentMode && familyId != null && targetClientId == null;
  const { family, isLoading: isFamilyLoading } = useFamily(
    familyDefaultMode ? familyId : undefined,
  );
  const familyAnchorClientId = focusedFamilyMemberId ?? family?.mainFamilyMember.clientId;

  const needsFallbackClient = !familyDefaultMode && targetClientId == null;
  const unpaginatedClients = useClients(
    {},
    { enabled: !isFamilyDocumentMode && needsFallbackClient && !flagEnabledPaginatedClients },
  );
  const paginatedClients = useClientsPaginated(
    {},
    { enabled: !isFamilyDocumentMode && needsFallbackClient && flagEnabledPaginatedClients },
  );
  const firstClientId = flagEnabledPaginatedClients
    ? paginatedClients.items[0]?.id
    : unpaginatedClients.clients[0]?.id;
  const isLoadingClients = flagEnabledPaginatedClients
    ? paginatedClients.isLoading
    : unpaginatedClients.isLoading;
  let clientId: string | undefined;
  if (isFamilyDocumentMode) {
    clientId = undefined;
  } else if (familyDefaultMode) {
    clientId = familyAnchorClientId;
  } else {
    clientId = targetClientId ?? firstClientId;
  }
  const linkState = familyDefaultMode && familyId ? { familyId } : undefined;
  let documentOwner: DocumentEditOwner | null = null;
  if (isFamilyDocumentMode && familyId) {
    documentOwner = { familyId };
  } else if (clientId) {
    documentOwner = { clientId };
  }
  const canFetch = !!(documentId && documentContent && documentOwner && isDocumentMode);

  const { data, isLoading } = useQuery<GenerateSuggestionsResponse>({
    queryKey: ['suggested-actions', documentId],
    queryFn: async () => {
      if (!documentOwner) throw new Error('Document owner is required');
      const { data } = await api.post<GenerateSuggestionsResponse>(
        `documents/${documentId}/suggested-actions/generate`,
        { documentContent, ...documentOwner, conversationId },
      );
      return data;
    },
    enabled: canFetch,
    staleTime: Infinity,
  });

  const defaultActions: SiestaAction[] = clientId
    ? [
        {
          type: SiestaActionType.LINK,
          id: 'agenda',
          label: 'Meeting agenda',
          description: 'Includes client summary and topics for discussion',
          descriptionMode: 'hover',
          href: `/client/${clientId}/meeting-agenda`,
          linkState,
        },
        {
          type: SiestaActionType.LINK,
          id: 'meeting-note',
          label: 'Meeting note',
          description: 'Generate notes from meeting recordings',
          descriptionMode: 'hover',
          href: `/client/${clientId}/meeting-note`,
          linkState,
        },
        {
          type: SiestaActionType.LINK,
          id: 'data-export',
          label: 'Data export',
          description: "Export a data export populated with the client's data",
          descriptionMode: 'hover',
          href: `/client/${clientId}/data-export`,
          linkState,
        },
        {
          type: SiestaActionType.LINK,
          id: 'suitability-report',
          label: 'Suitability report',
          description:
            'Generate a suitability report for your client based on the information we currently hold',
          descriptionMode: 'hover',
          href: `/client/${clientId}/suitability-report`,
          linkState,
        },
      ]
    : [];

  if (data?.suggestions?.length) {
    return {
      actions: data.suggestions.map((text: string) => ({
        type: SiestaActionType.CHAT,
        id: suggestedActionId(documentId, text),
        label: stripMarkdown(text),
      })),
      isLoading: false,
    };
  }

  const configuredActions = pageType ? SIESTA_ACTIONS_CONFIG[pageType] : undefined;
  const fallback = configuredActions ?? (isFamilyDocumentMode ? [] : defaultActions);

  return {
    actions: fallback,
    isLoading:
      (canFetch && isLoading) ||
      (!isFamilyDocumentMode && clientId == null && (isLoadingClients || isFamilyLoading)),
  };
}

// Ids seed the session's used-action set, so they must not repeat across documents or across
// regenerations of the same document — a positional id blanks the next set that reuses it.
function suggestedActionId(documentId: string | undefined, text: string) {
  return `ai-${hash(`${documentId ?? ''}:${text}`)}`;
}

// FNV-1a, 32-bit: the magic numbers are the algorithm's offset basis and prime. Math.imul keeps the
// multiply in 32-bit integer space, where a plain `*` would promote to a double and drop the low
// bits; `>>> 0` reads the signed result back as unsigned before base36 shortens it.
function hash(value: string) {
  let hashed = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hashed ^= value.charCodeAt(i);
    hashed = Math.imul(hashed, 16777619);
  }
  return (hashed >>> 0).toString(36);
}
